/**
 * useGestesDiscussion.ts — ETAPES.AVANCEMENT.1 lot 3 (option D, réponses de Carl du 23 oct. 12 h 50)
 * Les quatre gestes du critère, chacun par une route du serveur (REG-GESTE-CODE-1), puis relecture de /elements :
 *   Confirmer le sujet → POST sujet/confirmer ; Retenir une piste → POST pistes/{id}/retenir ;
 *   Approuver une décision → POST decisions/{id}/approuver ; Valider le plan de match → POST plan/valider.
 * Et les trois boutons du sujet candidat (DEC-INCOHERENCE-SIGNALEE-1) : sujet/garder, sujet/changer, sujet/combiner.
 */

import { useCallback, useMemo } from "react";
import { EVT_RELIRE_ELEMENTS, type ChoixGesteServeur } from "./useDiscussionElements";
import registre from "../../../../../registre/boites.json";  // lot 4, M5 : les actions par famille (BOITES.LECTURE.1)

export type ChoixSujet = "garder" | "changer" | "combiner";

export interface GestesDiscussion {
  confirmerSujet: () => Promise<boolean>;
  retenirPiste: (pisteId: number) => Promise<boolean>;
  approuverDecision: (decisionId: number) => Promise<boolean>;
  validerPlan: () => Promise<boolean>;
  trancherSujet: (choix: ChoixSujet) => Promise<boolean>;
  /** DEC-GREFFIER-AGENT-3 (règle B) : confirmer la précision du sujet proposée par l'agent ; S232 lot E3 : l'id de la précision montrée */
  confirmerPrecision: (precisionId?: number) => Promise<boolean>;
  /** DEC-DECIDEUR-1 : « Décider » (proposée → acceptée, approuvée si l'utilisateur est le décideur) et réponse à la question du décideur */
  deciderDecision: (decisionId: number) => Promise<boolean>;
  repondreDecideur: (decisionId: number, estDecideur: boolean, nom?: string) => Promise<boolean>;
  /** Lot 4, M5 (S226-PLAN-LOT4.md C.2) : les gestes d'un élément, chacun par sa route (routes_discussion_gestes.py, epingler d'api_rest.py) */
  accepterElement: (id: number) => Promise<boolean>;
  ecarterElement: (id: number, raison: string) => Promise<boolean>;
  modifierElement: (id: number, consigne?: string) => Promise<boolean>;
  /** S229 lot 7a : question facultative (DemandeAvis.question) — « Approfondir » d'un bloc du plan de match nomme le bloc */
  demanderAvis: (id: number, question?: string) => Promise<boolean>;
  versAction: (id: number) => Promise<boolean>;
  epinglerElement: (id: number) => Promise<boolean>;
  /** S229 lot G (DEC-POSER-QUESTION-1) : « Poser la question » d'une carte — bulle de Carlos écrite par le serveur, aucun message du client */
  poserQuestion: (texte: string) => Promise<boolean>;
  /** S229 lot 7d (DEC-LOT7-1, DEC-LOT7-3) : boutons du panneau final de E7 — bulle de Carlos écrite par le serveur, aucun message du client */
  creerChantier: () => Promise<boolean>;
  ajusterPlan: () => Promise<boolean>;
}

/** S229 lot G : la bulle de Carlos rendue par la route poser-question, montrée dans le chat par DiscussionWindow (injectVoiceMessage) */
export const EVT_BULLE_POSER_QUESTION = "bt-poser-question";

/** P-S232-31 n° 7 et 11 (REVUE-NUIT-S232) : un message court à l'écran (bandeau de ChatBoxV3, data-message-ecran, 4 s) — un geste refusé
 *  par le serveur n'est plus un console.warn muet ; un clic pendant que Carlos écrit dit pourquoi rien ne se passe. */
export const EVT_MESSAGE_ECRAN = "bt-message-ecran";
export function signalerEcran(texte: string): void {
  if (typeof window !== "undefined" && texte) window.dispatchEvent(new CustomEvent(EVT_MESSAGE_ECRAN, { detail: { texte } }));
}
/** Fonction pure (tests/test_s232_p31_refus_js.py) : le texte montré pour un refus de geste, selon le statut HTTP. */
export function texteRefus(statut: number): string {
  if (statut === 409) return "Ce geste n'est plus possible : la discussion a changé.";
  if (statut === 403) return "Ce geste ne vous est pas permis.";
  return `Le geste n'a pas abouti (${statut}).`;
}

/** S229 lot G (DEC-POSER-QUESTION-1, QCA-G1 : mécanisme 1) : POST poser-question ; la bulle rendue (texte et options) va au chat par
 *  EVT_BULLE_POSER_QUESTION, puis le workspace relit /elements. Faux si la route refuse (question inconnue, autre compte). */
export async function poserQuestionParLeCode(externalId: string, texte: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/v1/discussions/${externalId}/poser-question`, { method: "POST", headers: entetesDiscussion(), body: JSON.stringify({ texte }) });
    if (!res.ok) { console.warn(`[useGestesDiscussion] poser-question ${res.status}`); signalerEcran(texteRefus(res.status)); return false; }
    const bulle = (await res.json()) as { texte?: string; options?: string[] };
    window.dispatchEvent(new CustomEvent(EVT_BULLE_POSER_QUESTION, { detail: { texte: bulle.texte || "", options: bulle.options || [] } }));
    window.dispatchEvent(new CustomEvent(EVT_RELIRE_ELEMENTS));
    return true;
  } catch (err) {
    console.warn("[useGestesDiscussion] poser-question :", err);
    return false;
  }
}

/** Les gestes d'un élément sur sa carte ou dans le pied de sa boîte (riche.tsx Pilule) ; le geste plein des familles qui en ont un propre
 *  (Confirmer, Retenir, Décider, Valider) reste dans leur boîte. */
export type GesteElement = "accepter" | "modif" | "avis" | "ecarter" | "action" | "plan";
const ACCEPTER_ICI = ["fait", "document", "action"];  // piste : Retenir ; décision : Décider ; plan : Valider ; sujet : Confirmer (registre accepter_par_famille)
const MODIFIABLES = ["fait", "piste", "action", "decision", "avis_expert", "question_ouverte", "document"];  // greffier.FAMILLES_MODIFIER

/** Lot 4, M5 : les gestes permis pour un élément — les actions de sa famille au registre (contrat.elements[famille].actions), puis le
 *  passage d'état permis (C.2 ; mêmes refus que greffier.py) ; un geste non permis n'est pas rendu (absent, pas grisé). Le sujet garde ses gestes. */
export function gestesPermis(famille: string, etat: string | undefined): GesteElement[] {
  const el = (registre as unknown as { contrat: { elements: Record<string, { actions?: string[] }> } }).contrat.elements[famille];
  if (!el || famille === "sujet" || famille === "plan_de_match") return [];
  const a = el.actions || []; const e = etat || "proposee"; const vivant = e === "proposee" || e === "acceptee";
  const out: GesteElement[] = [];
  if (a.includes("accepter") && ACCEPTER_ICI.includes(famille) && e === "proposee") out.push("accepter");
  if (a.includes("modifier") && MODIFIABLES.includes(famille) && vivant) out.push("modif");
  if (a.includes("demander_expert") && famille !== "avis_expert") out.push("avis");
  if (a.includes("ecarter") && vivant) out.push("ecarter");
  if (a.includes("vers_action") && famille !== "action") out.push("action");
  if (a.includes("au_plan_de_match")) out.push("plan");
  return out;
}

/** En-têtes comme useDiscussionElements (jeton de session, sinon clé d'API). */
export function entetesDiscussion(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const jwt = localStorage.getItem("ghostx-jwt");
  if (jwt) headers["Authorization"] = `Bearer ${jwt}`;
  else {
    const key = (import.meta as unknown as Record<string, Record<string, string>>).env?.VITE_API_KEY;
    if (key) headers["X-API-Key"] = key;
  }
  return headers;
}

/** POST d'un geste ; vrai si la route répond 2xx. corps : décideur (DEC-DECIDEUR-1), sinon {}. */
export async function posterGeste(externalId: string, route: string, corps: Record<string, unknown> = {}): Promise<boolean> {
  try {
    const res = await fetch(`/api/v1/discussions/${externalId}/${route}`, { method: "POST", headers: entetesDiscussion(), body: JSON.stringify(corps) });
    if (!res.ok) { console.warn(`[useGestesDiscussion] ${route} ${res.status}`); signalerEcran(texteRefus(res.status)); }  // P-S232-31 n° 7
    return res.ok;
  } catch (err) {
    console.warn(`[useGestesDiscussion] ${route} :`, err);
    return false;
  }
}

/** S229 lot 7d (« Créer le chantier », « Ajuster d'abord ») : POST du geste, puis la bulle de Carlos rendue par la route (texte, options)
 *  s'affiche dans le chat par EVT_BULLE_POSER_QUESTION, comme « Poser la question » ; le workspace relit /elements. */
export async function gesteAvecBulle(externalId: string, route: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/v1/discussions/${externalId}/${route}`, { method: "POST", headers: entetesDiscussion(), body: "{}" });
    if (!res.ok) { console.warn(`[useGestesDiscussion] ${route} ${res.status}`); signalerEcran(texteRefus(res.status)); return false; }  // P-S232-31 n° 7
    const bulle = (await res.json()) as { texte?: string; options?: string[]; gestes?: unknown[] };
    enregistrerChoixBulles(externalId, bulle.gestes);  // P6 : les options de la bulle suivante gardent leur geste
    if (bulle.texte) window.dispatchEvent(new CustomEvent(EVT_BULLE_POSER_QUESTION, { detail: { texte: bulle.texte, options: bulle.options || [] } }));
    window.dispatchEvent(new CustomEvent(EVT_RELIRE_ELEMENTS));
    return true;
  } catch (err) {
    console.warn(`[useGestesDiscussion] ${route} :`, err);
    return false;
  }
}

/** S229 lot 7d (trou 1, DEC-CHANTIER-E8-1) : ce que fait l'écran d'un choix du geste en attente — « route » (appel sans message, la bulle
 *  suivante vient du code), « voir_chantier », « conception », « rester » (actions de l'écran), sinon « message » (route puis message, lot H). */
export function actionDuChoix(choix: Partial<ChoixGesteServeur> | null | undefined): "route" | "voir_chantier" | "conception" | "rester" | "message" {
  if (!choix || !choix.sans_message) return "message";
  if (choix.route) return "route";
  return choix.action_ecran === "voir_chantier" || choix.action_ecran === "conception" ? choix.action_ecran : "rester";
}

/** DEC-GREFFIER-AGENT-3 (règle C) : le choix sous la bulle dont le texte envoyé commence par son texte (l'option peut porter une précision,
 *  « Non — Julie ») ; null si le texte n'est pas celui d'un choix du geste. */
export function choixDuTexte(choix: ChoixGesteServeur[], texte: string): ChoixGesteServeur | null {
  const t = texte.trim();
  return choix.find((c) => t === c.texte || t.startsWith(`${c.texte} — `)) || null;
}

/** S229 lot H (DEC-CONFIRMER-OPTION-1) : le choix du geste en attente que porte une option cliquée sous la bulle — son texte exact
 *  (choixDuTexte), l'élément seul quand le choix est « Préfixe : élément » (une piste ou une décision proposée par son titre), ou, pour le
 *  sujet à confirmer, toute option « Confirmer : … » (le texte de l'option K2 vient de l'énoncé du sujet, celui du choix de son objectif). */
export function choixDeLOption(geste: { geste?: string; choix?: ChoixGesteServeur[] } | null | undefined, texte: string): ChoixGesteServeur | null {
  const choix = geste?.choix || [];
  const t = texte.trim();
  if (!t || !choix.length) return null;
  return choixDuTexte(choix, t) || choix.find((c) => c.texte.endsWith(` : ${t}`))
    || (geste?.geste === "confirmer_sujet" && t.startsWith("Confirmer : ") ? choix[0] : null) || null;
}

/** S229 lot 7e (P6) : les gestes des bulles écrites par le code, par fil puis par libellé d'option — lus dans la bulle elle-même (extra.options[]
 *  .geste relu par /messages, « gestes » de /elements bulle_code ou de la réponse d'une route), jamais seulement dans /elements. */
const CHOIX_BULLES = new Map<string, ChoixGesteServeur>();

/** Enregistre les gestes d'une bulle : options de /messages ({ label, geste }) ou choix rendus par une route ({ texte, route | action_ecran }). */
export function enregistrerChoixBulles(fil: string | null | undefined, options: unknown[] | null | undefined): void {
  if (!fil) return;
  for (const o of options || []) {
    const x = (o || {}) as { label?: string; geste?: ChoixGesteServeur } & Partial<ChoixGesteServeur>;
    const c = x.geste || (x.route || x.action_ecran ? (x as ChoixGesteServeur) : null);
    const lib = x.label || c?.texte;
    if (c && lib) CHOIX_BULLES.set(`${fil}::${lib}`, c);
  }
}

/** S229 lot 7e (P5, E7.dc.html:19) : les options de la bulle de fin de plan (choix « creer » et « ajuster » portés par la bulle) se dessinent
 *  en deux boutons ; rend leurs ids dans l'ordre, ou null pour toute autre bulle (ses options ne changent pas, GESTES.LOGIQUE.1). */
export function boutonsDeBulle(fil: string | null | undefined, options: string[]): string[] | null {
  if (!fil || options.length !== 2) return null;
  const ids = options.map((o) => CHOIX_BULLES.get(`${fil}::${o.trim()}`)?.id || "");
  return ids[0] === "creer" && ids[1] === "ajuster" ? ids : null;
}

/** Le choix d'une option du chat — d'abord le geste porté par la bulle (P6 : sans attendre /elements), puis le geste en attente, puis le geste
 *  du plan (/elements geste_du_plan, lot 7d) ; null : texte libre, envoyé comme message. */
export function choixDuChat(el: { geste_en_attente?: GesteChat | null; geste_du_plan?: GesteChat | null } | null | undefined,
  texte: string, fil?: string | null): ChoixGesteServeur | null {
  return (fil ? CHOIX_BULLES.get(`${fil}::${texte.trim()}`) : undefined)
    || choixDeLOption(el?.geste_en_attente, texte) || choixDeLOption(el?.geste_du_plan, texte);
}
type GesteChat = { geste?: string; element_id?: number | string; choix?: ChoixGesteServeur[] };

/** S230 lot A (C1, point 5) : une bulle écrite par le code dont une option porte le geste encore en attente (ex. « Votre sujet a évolué » →
 *  sujet/precision/confirmer) garde ses options actives sous une bulle plus récente, jusqu'à ce que le geste soit fait. */
export function bulleAuGesteEnAttente(el: { geste_en_attente?: GesteChat | null } | null | undefined, options: string[],
  fil?: string | null): boolean {
  const g = el?.geste_en_attente;
  if (!g || g.element_id === undefined || g.element_id === null || !fil) return false;
  return (options || []).some((o) => {
    const c = CHOIX_BULLES.get(`${fil}::${(o || "").trim()}`);
    return !!c && !!c.sans_message && String(c.element_id) === String(g.element_id);
  });
}

/** Une option de geste cliquée dans le chat : la même route que la pilule (avec le nom du décideur pris dans la précision), puis les deux
 *  zones relisent /elements. Sans nom pour « Non », la route n'est pas appelée : la boîte F39 demande le nom (DEC-DECIDEUR-1). */
/** S232 lot E3 (essai 4) : le corps du geste « Confirmer la précision » — l'id de la précision que la boîte montrait, pour que le serveur
 *  confirme ce texte-là même si le rangement l'a remplacée entre l'affichage et le clic ; sans id : pas de corps (comme avant). */
export function corpsConfirmerPrecision(precisionId?: number | null): Record<string, unknown> | undefined {
  return typeof precisionId === "number" && Number.isFinite(precisionId) ? { precision_id: precisionId } : undefined;
}

export async function executerChoixGeste(externalId: string, choix: ChoixGesteServeur, texte: string): Promise<boolean> {
  let corps = choix.corps || {};
  if (choix.demande_nom) {
    const nom = texte.trim().slice(choix.texte.length).replace(/^\s*—\s*/, "").trim();
    if (!nom) { window.dispatchEvent(new CustomEvent("bt-decideur-nom", { detail: { decisionId: choix.element_id } })); return false; }
    corps = { ...corps, nom };
  }
  if (!choix.route) return false;  // S229 lot 7b : action de l'écran, sans route (DiscussionWindow, actionDuChoix)
  if (choix.sans_message) return gesteAvecBulle(externalId, choix.route);  // la bulle suivante vient du code
  const ok = await posterGeste(externalId, choix.route, corps);
  window.dispatchEvent(new CustomEvent(EVT_RELIRE_ELEMENTS));
  return ok;
}

export function useGestesDiscussion(
  activeThreadId: string | null | undefined,
  recharger?: () => void,
): GestesDiscussion | null {
  const geste = useCallback(async (route: string, corps?: Record<string, unknown>) => {
    if (!activeThreadId) return false;
    const ok = await posterGeste(activeThreadId, route, corps);
    recharger?.();
    window.dispatchEvent(new CustomEvent(EVT_RELIRE_ELEMENTS));  // règle C : les options du chat suivent le geste fait dans le workspace
    return ok;
  }, [activeThreadId, recharger]);

  return useMemo(() => (activeThreadId ? {
    confirmerSujet: () => geste("sujet/confirmer"),
    retenirPiste: (id: number) => geste(`pistes/${id}/retenir`),
    approuverDecision: (id: number) => geste(`decisions/${id}/approuver`),
    validerPlan: () => geste("plan/valider"),
    trancherSujet: (choix: ChoixSujet) => geste(`sujet/${choix}`),
    confirmerPrecision: (precisionId?: number) => geste("sujet/precision/confirmer", corpsConfirmerPrecision(precisionId)),
    deciderDecision: (id: number) => geste(`decisions/${id}/decider`),
    repondreDecideur: (id: number, estDecideur: boolean, nom = "") => geste(`decisions/${id}/decideur`, { est_decideur: estDecideur, nom }),
    accepterElement: (id: number) => geste(`elements/${id}/accepter`),
    ecarterElement: (id: number, raison: string) => geste(`elements/${id}/ecarter`, { raison }),
    modifierElement: (id: number, consigne = "") => geste(`elements/${id}/modification`, { consigne }),
    demanderAvis: (id: number, question?: string) => geste(`elements/${id}/avis`, question ? { question } : undefined),
    versAction: (id: number) => geste(`elements/${id}/vers-action`),
    epinglerElement: (id: number) => geste(`elements/${id}/epingler`),
    poserQuestion: (texte: string) => poserQuestionParLeCode(activeThreadId, texte),
    creerChantier: () => gesteAvecBulle(activeThreadId, "plan/creer-chantier"),
    ajusterPlan: () => gesteAvecBulle(activeThreadId, "plan/ajuster"),
  } : null), [activeThreadId, geste]);
}
